// Image comparisons (before/after sliders). Extraction turns them into
// <figure data-compare><img><img><figcaption>; this makes that figure a slider: the second
// image lies over the first and shows to the right of a divider the reader moves (drag, or
// tap where it should go; arrow keys when focused). Without script the two images stack.
//
// Moving the divider only moves layers: the second image sits in a frame that slides right
// while the image inside slides left by the same amount, and the line slides with it. No
// repaint, no layout, at most one update per frame, so a finger drag stays smooth even on a
// 2400-pixel photo in a phone's WebView.

/**
 * The phone keeps its page swiper out of gestures that start on a slider by watching where the
 * `.cmp` boxes are (its reader page reports them, with wide tables). [tick], when given, is called as a dragged divider reaches the middle or either edge, for a
 * light haptic tick on a phone.
 */
export function enableCompare(root: ParentNode, tick?: () => void): () => void {
  const cleanup: (() => void)[] = [];
  for (const fig of root.querySelectorAll<HTMLElement>("figure[data-compare]")) {
    const imgs = Array.from(fig.querySelectorAll<HTMLImageElement>(":scope > img"));
    if (fig.querySelector(".cmp") || imgs.length !== 2) continue;
    const first = imgs[0]!, second = imgs[1]!;
    const doc = fig.ownerDocument;
    const box = doc.createElement("div");
    box.className = "cmp";
    box.setAttribute("role", "slider");
    box.setAttribute("tabindex", "0");
    box.setAttribute("aria-label", "Compare the two images");
    box.setAttribute("aria-valuemin", "0");
    box.setAttribute("aria-valuemax", "100");
    fig.insertBefore(box, first);
    const over = doc.createElement("div");
    over.className = "cmp-over";
    over.append(second);
    const line = doc.createElement("span");
    line.className = "cmp-line";
    box.append(first, over, line);

    let at = 50, width = 0, frame = 0, glide = 0, dragging = false;
    const draw = () => {
      frame = 0;
      width = width || box.getBoundingClientRect().width;
      const x = (width * at) / 100;
      over.style.transform = `translate3d(${x}px,0,0)`;
      second.style.transform = `translate3d(${-x}px,0,0)`;
      line.style.transform = `translate3d(${x}px,0,0)`;
      box.setAttribute("aria-valuenow", String(Math.round(at)));
    };
    const place = (pct: number) => {
      const was = at;
      at = Math.min(100, Math.max(0, pct));
      // a tick where the divider crosses the middle or comes to an edge, never while it rests there
      if (dragging && tick && ((was - 50) * (at - 50) < 0 || (at !== was && (at === 0 || at === 100)))) tick();
      if (!frame) frame = requestAnimationFrame(draw); // at most once a frame, however fast the finger
    };
    let left = 0;
    const toX = (x: number) => place(((x - left) / width) * 100);
    place(50);
    // a new width (rotation, a wider window) re-measures on the next placement
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => { width = 0; place(at); }) : undefined;
    observer?.observe(box);

    box.addEventListener("pointerdown", (e: PointerEvent) => {
      const r = box.getBoundingClientRect();
      left = r.left; width = r.width;
      box.classList.add("glide"); // a tap glides the divider to the finger; a drag then follows it 1:1
      clearTimeout(glide);
      glide = window.setTimeout(() => box.classList.remove("glide"), 180);
      box.setPointerCapture(e.pointerId);
      toX(e.clientX);
      dragging = true;
    });
    const release = () => { dragging = false; };
    box.addEventListener("pointerup", release);
    box.addEventListener("pointercancel", release);
    box.addEventListener("pointermove", (e: PointerEvent) => {
      if (!box.hasPointerCapture(e.pointerId)) return;
      box.classList.remove("glide");
      toX(e.clientX);
    });
    box.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") { e.preventDefault(); e.stopPropagation(); place(at + (e.key === "ArrowLeft" ? -5 : 5)); }
    });
    cleanup.push(() => {
      observer?.disconnect(); cancelAnimationFrame(frame); clearTimeout(glide);
      second.style.transform = "";
      box.replaceWith(first, second);
    });
  }
  return () => cleanup.splice(0).forEach((dispose) => dispose());
}
